// Drawola Studio: the "manage shareable links" dialog. Fork-only.
//
// Same overlay + panel markup as `StudioConfirmDialog`, so it looks native to
// the Studio. Owns the small local state the list needs (which row is busy,
// copy feedback) and rebuilds the link for the copy action exactly the way
// `exportToBackend` does.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";

import { useStudioT } from "./i18n";
import { SharesListView } from "./SharesListView";

import type { DocShare } from "../storage/types";

type Props = {
  docName: string;
  shares: DocShare[];
  onRevokeShare: (shareId: string) => Promise<void>;
  onClose: () => void;
};

export const StudioSharesDialog: React.FC<Props> = ({
  docName,
  shares,
  onRevokeShare,
  onClose,
}) => {
  const t = useStudioT();
  const [busyShareId, setBusyShareId] = useState<string | null>(null);
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const copyTimerRef = useRef<number>(0);

  useEffect(() => {
    return () => window.clearTimeout(copyTimerRef.current);
  }, []);

  const handleCopy = useCallback(
    async (share: DocShare) => {
      // Same shape as `exportToBackend` builds: origin + pathname + `#json=`.
      const link = `${window.location.origin}${window.location.pathname}#json=${share.id},${share.key}`;
      try {
        await copyTextToSystemClipboard(link);
      } catch {
        setError(t("shares.copyFailed"));
        return;
      }
      setError(null);
      setCopiedShareId(share.id);
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => {
        setCopiedShareId(null);
      }, 2000);
    },
    [t],
  );

  const handleRevoke = useCallback(
    async (share: DocShare) => {
      setBusyShareId(share.id);
      try {
        await onRevokeShare(share.id);
      } finally {
        setBusyShareId(null);
      }
    },
    [onRevokeShare],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // keep editor shortcuts from seeing dialog keystrokes
      event.stopPropagation();
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <div
      className="drawola-studio-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t("shares.title")}
      onKeyDown={onKeyDown}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="drawola-studio-dialog__panel drawola-studio-dialog__panel--shares">
        <h3 className="drawola-studio-dialog__title">{t("shares.title")}</h3>
        <p className="drawola-studio-dialog__body">{docName}</p>
        {error && (
          <div className="drawola-studio__error" role="alert">
            <span>{error}</span>
          </div>
        )}
        <SharesListView
          shares={shares}
          t={t}
          onCopy={handleCopy}
          onRevoke={handleRevoke}
          busyShareId={busyShareId}
          copiedShareId={copiedShareId}
        />
        <div className="drawola-studio-dialog__actions">
          <button
            type="button"
            className="drawola-studio-dialog__button"
            onClick={onClose}
          >
            {t("cloud.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};
